import { useEffect, useState } from 'react';
import { ensureMediaBlob } from '../../sync/mediaFetch';
import type { MediaRef, MonthKey } from '../../domain/types';

/**
 * IndexedDB から Blob を読み出して表示する。
 * キャッシュに無い場合（LRUで破棄済み・オフラインで未取得）はプレースホルダを出す。
 */
export function MediaImage({
  media,
  month,
  entryId,
  className,
  onClick,
}: {
  media: MediaRef;
  /** 未キャッシュのときに OneDrive から取りに行くために必要。 */
  month: MonthKey;
  entryId: string;
  className?: string;
  onClick?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    ensureMediaBlob(media, month, entryId).then((blob) => {
      if (cancelled) return;
      if (!blob) {
        setMissing(true);
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [media, month, entryId]);

  if (missing) {
    return (
      <div className={`media-missing ${className ?? ''}`} title="この端末にキャッシュがありません">
        <span>☁︎</span>
      </div>
    );
  }
  if (!url) return <div className={`media-loading ${className ?? ''}`} />;

  return (
    <img
      src={url}
      className={`${className ?? ''} media-${media.type}`}
      alt={media.type === 'drawing' ? '手書き' : '写真'}
      loading="lazy"
      decoding="async"
      onClick={onClick}
      style={onClick ? { cursor: 'zoom-in' } : undefined}
    />
  );
}

/** 拡大表示。左右キーとボタンで前後の画像へ移動できる。 */
export function Lightbox({
  media,
  month,
  entryId,
  index,
  onClose,
  onMove,
}: {
  media: MediaRef[];
  month: MonthKey;
  entryId: string;
  index: number;
  onClose: () => void;
  onMove: (next: number) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onMove(index - 1);
      if (e.key === 'ArrowRight' && index < media.length - 1) onMove(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, media.length, onClose, onMove]);

  const cur = media[index];
  if (!cur) return null;

  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label="閉じる">
        ✕
      </button>
      {index > 0 && (
        <button
          className="lightbox-nav prev"
          onClick={(e) => {
            e.stopPropagation();
            onMove(index - 1);
          }}
          aria-label="前の画像"
        >
          ‹
        </button>
      )}
      <div className="lightbox-body" onClick={(e) => e.stopPropagation()}>
        <MediaImage media={cur} month={month} entryId={entryId} className="lightbox-img" />
      </div>
      {index < media.length - 1 && (
        <button
          className="lightbox-nav next"
          onClick={(e) => {
            e.stopPropagation();
            onMove(index + 1);
          }}
          aria-label="次の画像"
        >
          ›
        </button>
      )}
      <span className="lightbox-count">
        {index + 1} / {media.length}
      </span>
    </div>
  );
}
